import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, http } from '../../api/client';
import { useAuth, type CurrentUser } from '../../api/auth';
import { Alert, Button, Field, FullPageLoader } from '../../components/ui';
import { AuthShell, PASSWORD_RULES, PasswordRules } from './AuthShell';

type ResetPreview = { email: string; fullName: string; expiresAt: string };

/**
 * აღდგენის ბმულით ახალი პაროლის დაყენება.
 *
 * წარმატებისას მომხმარებელი მაშინვე შედის — ერთი ნაბიჯით ნაკლები მაშინ,
 * როცა ისედაც დაბნეულია. სერვერი ამ დროს **ყველა ძველ სესიას აუქმებს**.
 */
export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [preview, setPreview] = useState<ResetPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    http
      .get<ResetPreview>(`/auth/reset-password/${token}`)
      .then((data) => alive && setPreview(data))
      .catch((err: unknown) => {
        if (!alive) return;
        setLoadError(err instanceof ApiError ? err.message : 'ბმულის შემოწმება ვერ მოხერხდა');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('პაროლები არ ემთხვევა');
      return;
    }
    if (!PASSWORD_RULES.every((r) => r.test(password))) {
      setError('პაროლი არ აკმაყოფილებს მოთხოვნებს');
      return;
    }

    setBusy(true);
    try {
      const res = await http.post<{ accessToken: string; user: CurrentUser }>(
        '/auth/reset-password',
        { token, password },
      );
      setSession(res.accessToken, res.user);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'პაროლის შეცვლა ვერ მოხერხდა');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <AuthShell title="პაროლის აღდგენა">
        <Alert kind="error">{loadError}</Alert>
        <p className="mt-4 text-center text-[13px] text-ink-mute">
          ბმულს ერთი საათი აქვს და მხოლოდ ერთხელ მუშაობს — მოითხოვე ახალი.
        </p>
        <Button
          variant="secondary"
          className="mt-4 w-full"
          onClick={() => navigate('/forgot-password')}
        >
          ახალი ბმულის მოთხოვნა
        </Button>
      </AuthShell>
    );
  }

  if (!preview) return <FullPageLoader label="ბმული მოწმდება…" />;

  return (
    <AuthShell title="ახალი პაროლი" subtitle="დააყენე ახალი პაროლი და შედი სისტემაში">
      <div className="mb-5 rounded-lg bg-brand-50 px-4 py-3">
        <p className="text-[13px] font-medium text-brand-800">{preview.fullName}</p>
        <p className="text-[12px] text-brand-700">{preview.email}</p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}

        <Field
          label="ახალი პაროლი"
          type="password"
          autoComplete="new-password"
          required
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <PasswordRules value={password} />

        <Field
          label="გაიმეორე პაროლი"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirm && confirm !== password ? 'პაროლები არ ემთხვევა' : undefined}
        />

        <Button type="submit" loading={busy} className="w-full">
          პაროლის შენახვა
        </Button>
      </form>
    </AuthShell>
  );
}
